import React, { useContext, useEffect, useState } from "react";

import {
  Grid,
  Paper,
  Avatar,
  Typography,
  Divider,
  Box,
  CircularProgress,
  IconButton,
  Collapse,
  Button,
  TablePagination,
} from "@material-ui/core";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import HistoryIcon from "@material-ui/icons/History";
import RefreshIcon from "@material-ui/icons/Refresh";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";

import io from "socket.io-client";

import { useStyles } from "./styles";

import { DashboardContext } from "../../../context/states/dashboardState";

const ActivityDashboard = () => {
  const classes = useStyles();

  const [expandedRow, setExpandedRow] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const {
    activityDashboard,
    activityDashboardLoading,
    getActivityDashboardHandler,
  } = useContext(DashboardContext);

  // ==========================================
  // ACTIVITY DATA FROM CONTEXT
  // ==========================================

  const activities = activityDashboard?.data || [];

  const summary = activityDashboard?.summary || {
    today: 0,
    updated: 0,
    created: 0,
    deleted: 0,
  };

  // ==========================================
  // INITIAL LOAD
  // ==========================================

  useEffect(() => {
    getActivityDashboardHandler(1, rowsPerPage);
  }, [getActivityDashboardHandler, rowsPerPage]);

  // ==========================================
  // SOCKET.IO
  // ==========================================

  useEffect(() => {
    const socket = io(`${process.env.REACT_APP_BASE_URL}`, {
      withCredentials: true,
    });

    socket.on("activity:created", (activity) => {
      // The GET API/context remains the source of truth.
      // Refresh dashboard data when a new activity is created.
      getActivityDashboardHandler();
    });

    return () => {
      socket.disconnect();
    };
  }, [getActivityDashboardHandler]);

  // ==========================================
  // FORMAT DATE
  // ==========================================

  const formatDate = (date) => {
    if (!date) {
      return "-";
    }

    return new Date(date).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // ==========================================
  // FORMAT VALUE
  // ==========================================

  const formatValue = (value) => {
    if (value === null || value === undefined || value === "") {
      return "-";
    }

    if (typeof value === "object") {
      return JSON.stringify(value);
    }

    return String(value);
  };

  // ==========================================
  // QUALIFICATION HTML TO READABLE TEXT
  // ==========================================

  const htmlToPlainText = (html) => {
    if (!html) return "";

    // Parse as HTML without inserting it into the page.
    const parsed = new DOMParser().parseFromString(String(html), "text/html");

    // Preserve paragraph breaks and line breaks.
    parsed.body.querySelectorAll("br").forEach((element) => {
      element.replaceWith(document.createTextNode("\n"));
    });

    parsed.body
      .querySelectorAll("p, div, li, h1, h2, h3, h4, h5, h6, blockquote")
      .forEach((element) => {
        element.appendChild(document.createTextNode("\n"));
      });

    return parsed.body.textContent
      .replace(/\u00a0/g, " ")
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  };

  // ==========================================
  // WORD-LEVEL DIFF
  // ==========================================

  const getQualificationDiff = (oldText, newText) => {
    const oldTokens = oldText.match(/\s+|[^\s]+/g) || [];
    const newTokens = newText.match(/\s+|[^\s]+/g) || [];

    // Avoid excessive memory use for very large summaries.
    if (oldTokens.length * newTokens.length > 40000) {
      return [
        ...oldTokens.map((text) => ({
          text,
          type: "removed",
        })),
        ...newTokens.map((text) => ({
          text,
          type: "added",
        })),
      ];
    }

    const rows = oldTokens.length + 1;
    const cols = newTokens.length + 1;

    const dp = Array.from({ length: rows }, () => new Uint16Array(cols));

    for (let i = oldTokens.length - 1; i >= 0; i--) {
      for (let j = newTokens.length - 1; j >= 0; j--) {
        if (oldTokens[i] === newTokens[j]) {
          dp[i][j] = dp[i + 1][j + 1] + 1;
        } else {
          dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
        }
      }
    }

    const diff = [];
    let i = 0;
    let j = 0;

    while (i < oldTokens.length && j < newTokens.length) {
      if (oldTokens[i] === newTokens[j]) {
        diff.push({
          text: oldTokens[i],
          type: "unchanged",
        });

        i++;
        j++;
      } else if (dp[i + 1][j] >= dp[i][j + 1]) {
        diff.push({
          text: oldTokens[i],
          type: "removed",
        });

        i++;
      } else {
        diff.push({
          text: newTokens[j],
          type: "added",
        });

        j++;
      }
    }

    while (i < oldTokens.length) {
      diff.push({
        text: oldTokens[i++],
        type: "removed",
      });
    }

    while (j < newTokens.length) {
      diff.push({
        text: newTokens[j++],
        type: "added",
      });
    }

    return diff;
  };

  // ==========================================
  // RENDER QUALIFICATION DIFFERENCES
  // ==========================================

  const renderQualificationDiff = (from, to) => {
    const oldText = htmlToPlainText(from);
    const newText = htmlToPlainText(to);

    const diff = getQualificationDiff(oldText, newText);

    if (diff.every((item) => item.type === "unchanged")) {
      return (
        <Typography variant="body2" color="textSecondary">
          No visible text changes.
        </Typography>
      );
    }

    return (
      <Box
        style={{
          padding: 12,
          border: "1px solid #e0e0e0",
          borderRadius: 4,
          whiteSpace: "pre-wrap",
          overflowWrap: "anywhere",
          lineHeight: 1.8,
          fontSize: 14,
        }}
      >
        {diff.map((item, index) => {
          if (item.type === "removed") {
            return (
              <span
                key={index}
                style={{
                  backgroundColor: "#fde8e7",
                  color: "#b71c1c",
                  textDecoration: "line-through",
                }}
              >
                {item.text}
              </span>
            );
          }

          if (item.type === "added") {
            return (
              <span
                key={index}
                style={{
                  backgroundColor: "#e4f5e9",
                  color: "#1b5e20",
                  fontWeight: 600,
                }}
              >
                {item.text}
              </span>
            );
          }

          return <span key={index}>{item.text}</span>;
        })}
      </Box>
    );
  };

  // ==========================================
  // ACTION CONFIG
  // ==========================================

  const getActionConfig = (action) => {
    switch (action) {
      case "CREATE":
        return {
          label: "Created",
          className: classes.created,
          icon: <AddCircleOutlineIcon />,
        };

      case "DELETE":
        return {
          label: "Deleted",
          className: classes.deleted,
          icon: <DeleteIcon />,
        };

      case "UPDATE":
      default:
        return {
          label: "Updated",
          className: classes.updated,
          icon: <EditIcon />,
        };
    }
  };

  // ==========================================
  // RENDER CHANGES
  // ==========================================

  // ==========================================
  // RENDER CHANGES
  // ==========================================

  const formatFieldName = (field) => {
    const labels = {
      "rates.service.translation.rate.urgent.perWord":
        "Translation — Urgent — Price per Word",

      "rates.service.translation.rate.urgent.minCharges":
        "Translation — Urgent — Minimum Charges",

      "rates.service.translation.rate.nonUrgent.perWord":
        "Translation — Non-Urgent — Price per Word",

      "rates.service.translation.rate.nonUrgent.minCharges":
        "Translation — Non-Urgent — Minimum Charges",

      "rates.service.onsite.rate.attendance": "Onsite — Attendance Rate",

      "rates.service.onsite.rate.waiting": "Onsite — Waiting Rate",

      "rates.service.onsite.rate.travelIn": "Onsite — Travel In",

      "rates.service.onsite.rate.travelOut": "Onsite — Travel Out",

      "rates.service.onsite.rate.milage": "Onsite — Mileage",

      "rates.service.video": "Video Call Rate",
      "rates.service.vWaiting": "Video Call Waiting Rate",
      "rates.service.telephone": "Telephone Rate",
      "rates.service.tWaiting": "Telephone Waiting Rate",

      "rates.service.transcription.urgent": "Transcription — Urgent Rate",

      "rates.service.transcription.nonUrgent":
        "Transcription — Non-Urgent Rate",

      "rates.service.transcription.rate.mins05.urgent":
        "Transcription — 0–5 Minutes — Urgent",

      "rates.service.transcription.rate.mins05.nonUrgent":
        "Transcription — 0–5 Minutes — Non-Urgent",

      "rates.service.transcription.rate.mins510.urgent":
        "Transcription — 5–10 Minutes — Urgent",

      "rates.service.transcription.rate.mins510.nonUrgent":
        "Transcription — 5–10 Minutes — Non-Urgent",

      "rates.service.transcription.rate.mins1015.urgent":
        "Transcription — 10–15 Minutes — Urgent",

      "rates.service.transcription.rate.mins1015.nonUrgent":
        "Transcription — 10–15 Minutes — Non-Urgent",

      "rates.service.transcription.rate.mins1530.urgent":
        "Transcription — 15–30 Minutes — Urgent",

      "rates.service.transcription.rate.mins1530.nonUrgent":
        "Transcription — 15–30 Minutes — Non-Urgent",

      "rates.service.transcription.rate.mins3045.urgent":
        "Transcription — 30–45 Minutes — Urgent",

      "rates.service.transcription.rate.mins3045.nonUrgent":
        "Transcription — 30–45 Minutes — Non-Urgent",

      "rates.service.transcription.rate.hour4501.urgent":
        "Transcription — 45 Minutes to 1 Hour — Urgent",

      "rates.service.transcription.rate.hour4501.nonUrgent":
        "Transcription — 45 Minutes to 1 Hour — Non-Urgent",

      multipleRates: "Multiple Rates",
    };

    if (labels[field]) return labels[field];

    // Fallback for other rate fields, including nested transcription rates.
    return field
      .replace(/^rates\.service\./, "")
      .replace(/^rates\./, "")
      .replace(/\./g, " — ")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const renderChanges = (changes, parentField = "") => {
    if (
      !changes ||
      typeof changes !== "object" ||
      Object.keys(changes).length === 0
    ) {
      return (
        <Typography variant="body2" color="textSecondary">
          No field changes recorded.
        </Typography>
      );
    }

    // Rate changes stored as an array.
    if (Array.isArray(changes.fields)) {
      if (changes.fields.length === 0) {
        return (
          <Typography variant="body2" color="textSecondary">
            No field changes recorded.
          </Typography>
        );
      }

      return changes.fields.map((item, index) => (
        <Box
          key={`${item.field || "field"}-${index}`}
          className={classes.changeRow}
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 8,
            padding: "8px 0",
          }}
        >
          <Typography className={classes.changeField}>
            {formatFieldName(item.field || "Unknown Field")}
          </Typography>

          <Typography className={classes.oldValue}>
            {formatValue(item.from)}
          </Typography>

          <Typography className={classes.arrow}>→</Typography>

          <Typography className={classes.newValue}>
            {formatValue(item.to)}
          </Typography>
        </Box>
      ));
    }

    // Existing recursive change format.
    return Object.entries(changes).map(([field, change]) => {
      const fieldName = parentField ? `${parentField}.${field}` : field;

      // Qualification summary: show highlighted text differences.
      if (fieldName === "qual_summary") {
        return (
          <Box key={fieldName} style={{ padding: "12px 0", width: "100%" }}>
            <Typography
              className={classes.changeField}
              style={{ marginBottom: 8 }}
            >
              Qualification Summary
            </Typography>

            {renderQualificationDiff(change?.from, change?.to)}

            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              style={{ gap: 12, marginTop: 8 }}
            >
              <Typography variant="caption">
                <span
                  style={{
                    color: "#b71c1c",
                    backgroundColor: "#fde8e7",
                    padding: "2px 5px",
                  }}
                >
                  Removed text
                </span>
              </Typography>

              <Typography variant="caption">
                <span
                  style={{
                    color: "#1b5e20",
                    backgroundColor: "#e4f5e9",
                    padding: "2px 5px",
                  }}
                >
                  Added text
                </span>
              </Typography>
            </Box>
          </Box>
        );
      }

      // Nested object that is not a { from, to } change.
      if (
        change &&
        typeof change === "object" &&
        !Array.isArray(change) &&
        !Object.prototype.hasOwnProperty.call(change, "from") &&
        !Object.prototype.hasOwnProperty.call(change, "to")
      ) {
        return (
          <Box key={fieldName}>
            <Typography
              className={classes.changeField}
              style={{
                fontWeight: 600,
                marginTop: parentField ? 8 : 0,
                marginBottom: 4,
              }}
            >
              {field}
            </Typography>

            <Box style={{ paddingLeft: parentField ? 0 : 16 }}>
              {renderChanges(change, fieldName)}
            </Box>
          </Box>
        );
      }

      // Normal changes for all other fields.
      return (
        <Box key={fieldName} className={classes.changeRow}>
          <Typography className={classes.changeField}>{field}</Typography>

          <Typography className={classes.oldValue}>
            {formatValue(change?.from)}
          </Typography>

          <Typography className={classes.arrow}>→</Typography>

          <Typography className={classes.newValue}>
            {formatValue(change?.to)}
          </Typography>
        </Box>
      );
    });
  };

  // ==========================================
  // ACTIVITY SUMMARY CARDS
  // ==========================================

  const summaryCards = [
    {
      name: "Today's Activity",
      value: summary.today || 0,
      icon: <HistoryIcon />,
      className: "activity",
    },
    {
      name: "Updated",
      value: summary.updated || 0,
      icon: <EditIcon />,
      className: "updated",
    },
    {
      name: "Created",
      value: summary.created || 0,
      icon: <AddCircleOutlineIcon />,
      className: "created",
    },
    {
      name: "Deleted",
      value: summary.deleted || 0,
      icon: <DeleteIcon />,
      className: "deleted",
    },
  ];

  // ==========================================
  // LOADING
  // ==========================================

  if (activityDashboardLoading && activities.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="300px"
      >
        <CircularProgress />
      </Box>
    );
  }

  const pagination = activityDashboard?.pagination || {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  };

  const handleChangePage = (event, newPage) => {
    getActivityDashboardHandler(newPage + 1, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);

    setRowsPerPage(newRowsPerPage);

    getActivityDashboardHandler(1, newRowsPerPage);
  };

  // ==========================================
  // RENDER
  // ==========================================

  return (
    <Grid container spacing={1}>
      {/* ======================================
          HEADER
      ====================================== */}

      <Grid item xs={12}>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Typography variant="h6">Activity Overview</Typography>
          </Grid>

          <Grid item>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={getActivityDashboardHandler}
              disabled={activityDashboardLoading}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Grid>

      {/* ======================================
          SUMMARY CARDS
      ====================================== */}

      {summaryCards.map((card, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          <Paper elevation={1} className={classes.paper}>
            <div className={classes.nameDiv}>
              <Typography variant="h6">{card.name}</Typography>

              <Typography variant="h4" className={classes.amount}>
                {card.value}
              </Typography>

              <Typography className={classes.count}>Activities</Typography>
            </div>

            <Avatar className={classes[card.className]}>{card.icon}</Avatar>
          </Paper>
        </Grid>
      ))}

      <Grid item xs={12}>
        <Divider />
      </Grid>

      {/* ======================================
          RECENT ACTIVITY
      ====================================== */}

      <Grid item xs={12}>
        <Typography variant="h6">Recent Activity</Typography>
      </Grid>

      <Grid item xs={12}>
        <Paper elevation={1} className={classes.activityPaper}>
          {activities.length === 0 ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="150px"
            >
              <Typography color="textSecondary">No activity found.</Typography>
            </Box>
          ) : (
            activities.map((activity) => {
              const config = getActionConfig(activity.action);
              const expanded = expandedRow === activity._id;

              return (
                <React.Fragment key={activity._id}>
                  {/* ACTIVITY ROW */}

                  <Box className={classes.activityRow}>
                    <Avatar className={config.className}>{config.icon}</Avatar>

                    {/* MAIN CONTENT */}

                    <Box className={classes.activityContent}>
                      <Box display="flex" alignItems="center" flexWrap="wrap">
                        <Typography className={classes.actionText}>
                          {config.label}
                        </Typography>

                        <Typography className={classes.moduleText}>
                          {activity.module}
                        </Typography>

                        <Typography className={classes.referenceText}>
                          {activity.recordRef || "-"}
                        </Typography>
                      </Box>

                      <Typography className={classes.description}>
                        {activity.description}
                      </Typography>

                      <Box display="flex" alignItems="center" flexWrap="wrap">
                        <Typography className={classes.userText}>
                          {activity.userName || activity.user?.fullName || "-"}
                        </Typography>

                        <Typography className={classes.roleText}>
                          {activity.userRole || activity.user?.role || "-"}
                        </Typography>

                        <Typography className={classes.dateText}>
                          {formatDate(activity.createdAt)}
                        </Typography>
                      </Box>
                    </Box>

                    {/* EXPAND */}

                    <IconButton
                      size="small"
                      onClick={() =>
                        setExpandedRow(expanded ? null : activity._id)
                      }
                    >
                      {expanded ? (
                        <KeyboardArrowUpIcon />
                      ) : (
                        <KeyboardArrowDownIcon />
                      )}
                    </IconButton>
                  </Box>

                  {/* DETAILS */}

                  <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Box className={classes.details}>
                      <Typography className={classes.detailsTitle}>
                        Changes
                      </Typography>

                      {renderChanges(activity.changes)}

                      {activity.metadata && (
                        <Box className={classes.metadata}>
                          <Typography className={classes.detailsTitle}>
                            Details
                          </Typography>

                          <Typography variant="body2">
                            File Ref: {activity.metadata.fileRef || "-"}
                          </Typography>

                          <Typography variant="body2">
                            Client: {activity.metadata.clientName || "-"}
                          </Typography>

                          <Typography variant="body2">
                            Service: {activity.metadata.serviceType || "-"}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Collapse>

                  <Divider />
                </React.Fragment>
              );
            })
          )}
        </Paper>
      </Grid>

      {/* ======================================
          PAGINATION
      ====================================== */}

      {pagination.total > 0 && (
        <Grid item xs={12}>
          <Paper elevation={1}>
            <TablePagination
              component="div"
              count={pagination.total}
              page={(pagination.page || 1) - 1}
              onChangePage={handleChangePage}
              rowsPerPage={rowsPerPage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </Paper>
        </Grid>
      )}
    </Grid>
  );
};

export default ActivityDashboard;
